import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import apiManager from '../../../lib/api';
import trackingManager from '../../../lib/tracking';
import { EventType } from '../../../lib/tracking/eventTracker';
import { WishlistItem } from '../../../lib/api/models';
import { T } from '@tolgee/react';

interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
}

const FavoritesPage = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Cargar favoritos usando la API
  useEffect(() => {
    if (auth.user) {
      const fetchFavorites = async () => {
        setIsLoading(true);
        try {
          // Registrar evento de vista de página
          trackingManager.events.track(
            EventType.PAGE_VIEW,
            {
              path: window.location.pathname,
              pageTitle: document.title,
              customData: { section: 'wishlist' }
            }
          );
          
          // Rastrear evento personalizado
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'view_wishlist',
              userId: auth.user?.id
            }
          );
          
          // Obtener la lista de deseos del usuario
          const response = await apiManager.wishlist.getWishlist();
          
          if (response.success && response.data) {
            // Transformar los datos de la API al formato que espera el componente
            const items = response.data.items || [];
            
            // Obtener detalles completos de los productos
            const productsData = await Promise.all(
              items.map(async (item: WishlistItem) => {
                try {
                  const productResponse = await apiManager.products.getProduct(item.productId);
                  if (productResponse.success && productResponse.data) {
                    const product = productResponse.data;
                    return {
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      image: product.images && product.images.length > 0 
                        ? product.images[0].url 
                        : 'https://via.placeholder.com/500',
                      category: product.category ? product.category.name : 'Sin categoría'
                    };
                  }
                  return null;
                } catch (error) {
                  console.error(`Error al obtener detalles del producto ${item.productId}:`, error);
                  return null;
                }
              })
            );
            
            // Filtrar resultados nulos
            setFavorites(productsData.filter(p => p !== null) as Product[]);
          } else {
            console.error('Error al cargar favoritos:', response.error);
            setFavorites([]);
          }
        } catch (error) {
          console.error('Error al cargar favoritos:', error);
          setFavorites([]);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchFavorites();
    }
  }, [auth.user]);
  
  const handleRemoveFavorite = async (id: string) => {
    try {
      // Registrar evento de eliminación de favorito
      trackingManager.events.track(
        EventType.REMOVE_FROM_WISHLIST,
        {
          userId: auth.user?.id,
          productId: id
        }
      );
      
      // Llamar a la API para eliminar de la lista de deseos
      const response = await apiManager.wishlist.removeFromWishlist(id);
      
      if (response.success) {
        // Actualizar la interfaz
        setFavorites(favorites.filter(product => product.id !== id));
      } else {
        console.error('Error al eliminar de favoritos:', response.error);
      }
    } catch (error) {
      console.error('Error al eliminar de favoritos:', error);
    }
  };
  
  const handleAddToCart = async (productId: string) => {
    try {
      // Registrar evento de añadir al carrito desde favoritos
      trackingManager.events.track(
        EventType.ADD_TO_CART,
        {
          userId: auth.user?.id,
          productId: productId,
          source: 'wishlist'
        }
      );
      
      // Obtener detalles del producto
      const productResponse = await apiManager.products.getProduct(productId);
      
      if (productResponse.success && productResponse.data) {
        // Añadir al carrito (asumiendo que el primer variant es el que queremos)
        await apiManager.cart.addToCart(productId, 1);
        
        // Mostrar notificación o feedback
        alert('Producto añadido al carrito');
      }
    } catch (error) {
      console.error('Error al añadir al carrito:', error);
    }
  };
  
  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link
            to="/account"
            className="text-sm font-medium text-black hover:text-gray-700"
            onClick={() => trackingManager.events.track(
              EventType.CUSTOM,
              {
                eventName: 'wishlist_back_to_account',
                userId: auth.user?.id
              }
            )}
          >
            ← <T keyName="auth.favorites.back_to_account">Volver a mi cuenta</T>
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900"><T keyName="auth.favorites.title">Mis favoritos</T></h1>
            <p className="mt-1 text-sm text-gray-500"><T keyName="auth.favorites.subtitle">Productos que has guardado como favoritos.</T></p>
          </div>
          
          <div className="px-6 py-6">
            {isLoading ? (
              // Esqueleto de carga
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 h-64 rounded-lg"></div>
                    <div className="mt-4 flex justify-between">
                      <div className="h-10 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : favorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="relative group"
                  >
                    <div className="aspect-w-1 aspect-h-1 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-64 object-cover object-center group-hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          onClick={() => handleRemoveFavorite(product.id)}
                          className="p-1.5 bg-white rounded-full shadow-sm hover:bg-red-50 transition-colors focus:outline-none"
                          title="Eliminar de favoritos"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-sm text-gray-300">{product.category}</p>
                            <h3 className="text-base font-medium text-white truncate">{product.name}</h3>
                          </div>
                          <p className="text-base font-medium text-white">{(product.price || 0).toFixed(2)} €</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-between">
                      <Link
                        to={`/products/${product.id}`}
                        onClick={() => trackingManager.events.track(
                          EventType.CUSTOM,
                          {
                            eventName: 'wishlist_view_product',
                            userId: auth.user?.id,
                            productId: product.id
                          }
                        )}
                        className="py-2 px-4 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                      >
                        <T keyName="auth.favorites.view_details">Ver detalles</T>
                      </Link>
                      <button
                        onClick={() => handleAddToCart(product.id)}
                        className="py-2 px-4 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                      >
                        <T keyName="auth.favorites.add_to_cart">Añadir al carrito</T>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900"><T keyName="auth.favorites.no_favorites">No tienes favoritos todavía</T></h3>
                <p className="mt-1 text-sm text-gray-500"><T keyName="auth.favorites.no_favorites_message">Marca tus productos favoritos para verlos aquí</T></p>
                <div className="mt-6">
                  <Link
                    to="/products"
                    onClick={() => trackingManager.events.track(
                      EventType.CUSTOM,
                      {
                        eventName: 'empty_wishlist_explore',
                        userId: auth.user?.id
                      }
                    )}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                  >
                    <T keyName="auth.favorites.explore_products">Explorar productos</T>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FavoritesPage; 